import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { Button } from '@kop/ui';
import { Fingerprint, LockKeyhole, LoaderCircle } from 'lucide-react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type { AuthAdapter, AuthenticatedSession } from './types';
import { mobilePasskeyAvailable, mobilePasskeyEnvelope, mobilePasskeyOrigin, mobileSessionMediaQuery, mobileUnlockError, mustLockAfterAbsence } from './mobile-session-lock-model';
import '../features/field-mobile/mobile-session-lock.css';

/** UI re-entry lock; existing server identity and business authorization are retained. */
export function MobileSessionLock({ session, adapter, children }: {
  session: AuthenticatedSession;
  adapter: AuthAdapter;
  children: ReactNode;
}) {
  const [mobile, setMobile] = useState(() => window.matchMedia(mobileSessionMediaQuery).matches);
  const [freshAuthentication] = useState(() => mobile && Boolean(adapter.consumeFreshAuthentication?.(session.identity.id)));
  const [locked, setLocked] = useState(() => mobile && !freshAuthentication);
  const [mounted, setMounted] = useState(!locked);
  const [masked, setMasked] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [passkeyRegistered, setPasskeyRegistered] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyChecked, setPasskeyChecked] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  const [enrollmentOffer, setEnrollmentOffer] = useState(false);
  const [freshEnrollmentNeedsPassword, setFreshEnrollmentNeedsPassword] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const enrollmentDialog = useRef<HTMLDialogElement>(null);
  const hiddenAt = useRef<number | null>(null);
  const epoch = useRef(0);
  const passkeyCeremony = useRef(false);
  const automaticAttempted = useRef(false);
  const blocking = mobile && (locked || masked);

  useEffect(() => {
    const query = window.matchMedia(mobileSessionMediaQuery);
    const update = () => {
      if (query.matches) { setLocked(true); setPassword(''); }
      else setMounted(true);
      setMobile(query.matches);
    };
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!mobile) return;
    const hide = () => {
      hiddenAt.current ??= Date.now();
      setMasked(true);
      setPassword('');
      if (!passkeyCeremony.current) epoch.current += 1;
    };
    const visibility = () => {
      if (document.visibilityState === 'hidden') { hide(); return; }
      if (mustLockAfterAbsence(hiddenAt.current, Date.now())) { setEnrollmentOffer(false); setLocked(true); }
      hiddenAt.current = null;
      setMasked(false);
    };
    const pageHide = () => {
      passkeyCeremony.current = false;
      epoch.current += 1;
      setEnrollmentOffer(false);
      hiddenAt.current ??= Date.now();
      setMasked(true); setPassword(''); setLocked(true);
    };
    const pageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setLocked(true);
      visibility();
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', pageHide);
    window.addEventListener('pageshow', pageShow);
    return () => {
      epoch.current += 1;
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', pageHide);
      window.removeEventListener('pageshow', pageShow);
    };
  }, [mobile]);

  useEffect(() => {
    if (!mobile || (!locked && !freshAuthentication) || !mobilePasskeyAvailable(Boolean(adapter.executeMobilePasskey))) {
      setPasskeyReady(false);
      setPasskeyChecked(true);
      return;
    }
    let alive = true;
    setPasskeyChecked(false);
    void adapter.executeMobilePasskey!({ operation: 'status' }).then(value => {
      const status = mobilePasskeyEnvelope(value);
      if (alive) {
        const registered = status.registered === true;
        setPasskeyRegistered(registered); setPasskeyReady(true); setPasskeyChecked(true);
        if (freshAuthentication && !registered && localStorage.getItem(`kop-passkey-enrollment-declined:${session.identity.id}`) !== '1')
          setEnrollmentOffer(true);
      }
    }).catch(() => { if (alive) { setPasskeyReady(false); setPasskeyChecked(true); } });
    return () => { alive = false; };
  }, [adapter, freshAuthentication, locked, mobile, session.identity.id]);

  useLayoutEffect(() => {
    if (blocking && !dialog.current?.open) dialog.current?.showModal();
    else if (!blocking && dialog.current?.open) dialog.current.close();
  }, [blocking]);

  useLayoutEffect(() => {
    if (enrollmentOffer && !blocking && !enrollmentDialog.current?.open) enrollmentDialog.current?.showModal();
    else if ((!enrollmentOffer || blocking) && enrollmentDialog.current?.open) enrollmentDialog.current.close();
  }, [blocking, enrollmentOffer]);

  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (busy || !password) return;
    const attempt = epoch.current;
    setBusy(true);
    setError('');
    try {
      if (!adapter.reauthenticateWithPassword) throw new Error('AUTH_REAUTH_UNAVAILABLE');
      await adapter.reauthenticateWithPassword(password);
      if (epoch.current !== attempt || document.visibilityState === 'hidden') return;
      setMounted(true);
      setLocked(false);
    } catch (failure) {
      if (epoch.current === attempt) setError(mobileUnlockError(failure));
    } finally {
      setPassword('');
      setBusy(false);
    }
  }

  const authenticateWithPasskey = useCallback(async () => {
    if (busy || passkeyCeremony.current || !adapter.executeMobilePasskey) return;
    const attempt = epoch.current;
    passkeyCeremony.current = true;
    setBusy(true); setError('');
    try {
      const issued = mobilePasskeyEnvelope(await adapter.executeMobilePasskey({ operation: 'authentication-options' }));
      if (epoch.current !== attempt || document.visibilityState === 'hidden') return;
      const response = await startAuthentication({ optionsJSON: issued.options as Parameters<typeof startAuthentication>[0]['optionsJSON'] });
      const receipt = mobilePasskeyEnvelope(await adapter.executeMobilePasskey({ operation: 'authentication-verify', challengeId: issued.challengeId, response }));
      if (receipt.verified !== true || epoch.current !== attempt) throw new Error('PASSKEY_AUTHENTICATION_INVALID');
      setMounted(true); setLocked(false);
    } catch (failure) {
      if (epoch.current === attempt) setError(mobileUnlockError(failure));
    } finally { passkeyCeremony.current = false; setBusy(false); }
  }, [adapter, busy]);

  useEffect(() => {
    if (!locked) { automaticAttempted.current = false; setPasswordMode(false); return; }
    if (!mobile || masked || busy || !passkeyChecked || !passkeyReady || !passkeyRegistered || automaticAttempted.current || document.visibilityState === 'hidden') return;
    automaticAttempted.current = true;
    void authenticateWithPasskey();
  }, [authenticateWithPasskey, busy, locked, masked, mobile, passkeyChecked, passkeyReady, passkeyRegistered]);

  async function enrollPasskey(requirePassword = true) {
    if (busy || (requirePassword && !password) || !adapter.executeMobilePasskey || (requirePassword && !adapter.reauthenticateWithPassword)) return;
    const attempt = epoch.current;
    passkeyCeremony.current = true;
    setBusy(true); setError('');
    try {
      if (requirePassword) await adapter.reauthenticateWithPassword!(password);
      const issued = mobilePasskeyEnvelope(await adapter.executeMobilePasskey({ operation: 'registration-options' }));
      const response = await startRegistration({ optionsJSON: issued.options as Parameters<typeof startRegistration>[0]['optionsJSON'] });
      const receipt = mobilePasskeyEnvelope(await adapter.executeMobilePasskey({ operation: 'registration-verify', challengeId: issued.challengeId, response }));
      if (receipt.verified !== true || epoch.current !== attempt) throw new Error('PASSKEY_REGISTRATION_INVALID');
      localStorage.removeItem(`kop-passkey-enrollment-declined:${session.identity.id}`);
      setPasskeyRegistered(true); setEnrollmentOffer(false); setMounted(true); setLocked(false);
    } catch (failure) {
      if (epoch.current === attempt) {
        const code = failure && typeof failure === 'object' && 'details' in failure
          ? String((failure as { details?: { code?: unknown } }).details?.code ?? '')
          : failure instanceof Error ? failure.message : '';
        if (!requirePassword && code === 'PASSKEY_RECENT_PASSWORD_AUTH_REQUIRED')
          setFreshEnrollmentNeedsPassword(true);
        setError(mobileUnlockError(failure));
      }
    } finally { passkeyCeremony.current = false; setPassword(''); setBusy(false); }
  }

  return <>
    <div hidden={blocking} inert={blocking || undefined}>
      {mounted ? children : null}
    </div>
    <dialog ref={dialog} className="mobile-session-lock mobile-session-lock--compact" onCancel={event => event.preventDefault()} aria-labelledby="mobile-lock-title">
      <form onSubmit={event => void unlock(event)} className="mobile-session-lock__card">
        <span className="mobile-session-lock__brand">KOP · 안전보건시스템</span>
        <h1 id="mobile-lock-title" className="mobile-session-lock__sr-only">본인 확인</h1>
        {busy || !passkeyChecked ? <div className="mobile-session-lock__waiting" role="status"><LoaderCircle className="spin" size={30} aria-hidden="true" /><span>본인 확인 중</span></div> : passkeyReady && passkeyRegistered && !passwordMode ? <Button type="button" className="mobile-session-lock__biometric" aria-label="지문·얼굴 인증" onClick={() => void authenticateWithPasskey()} icon={<Fingerprint size={40} />} /> : null}
        {passkeyChecked && (passwordMode || !passkeyReady || !passkeyRegistered) && <>
        <label htmlFor="mobile-unlock-password">비밀번호</label>
        <input id="mobile-unlock-password" type="password" autoComplete="current-password" value={password}
          onChange={event => setPassword(event.target.value)} required disabled={busy} />
        <Button type="submit" disabled={busy || !password} aria-busy={busy}>{busy ? '확인 중…' : '잠금 해제'}</Button>
        </>}
        {error && <p role="alert" className="mobile-session-lock__error">{error}</p>}
        {passkeyChecked && passkeyReady && passkeyRegistered && !busy && <Button type="button" variant="quiet" onClick={() => { setPasswordMode(!passwordMode); setError(''); }}>{passwordMode ? '지문·얼굴 인증' : '비밀번호로 열기'}</Button>}
        <Button type="button" variant="quiet" disabled={busy} onClick={() => void adapter.signOut().catch(failure => setError(mobileUnlockError(failure)))}>로그아웃</Button>
        {passkeyReady && passwordMode && <details className="mobile-session-lock__settings"><summary>기기 인증 설정</summary>
          <Button type="button" variant="secondary" disabled={busy || !password} onClick={() => void enrollPasskey()}>{passkeyRegistered ? '비밀번호 확인 후 이 기기에 인증 등록' : '비밀번호 확인 후 지문·얼굴 등록'}</Button>
          <p className="mobile-session-lock__hint">기기에 따라 지문·얼굴 대신 화면 잠금 PIN을 사용할 수 있습니다.</p>
        </details>}
        {window.location.origin !== mobilePasskeyOrigin && <p className="mobile-session-lock__hint">기기 인증은 safety.kop.co.kr에서 사용할 수 있습니다.</p>}
      </form>
    </dialog>
    <dialog ref={enrollmentDialog} className="mobile-session-lock mobile-session-lock--enrollment" onCancel={event => event.preventDefault()} aria-labelledby="mobile-enrollment-title">
      <section className="mobile-session-lock__card">
        <LockKeyhole size={32} aria-hidden="true" />
        <h1 id="mobile-enrollment-title">지문·얼굴 인증 등록</h1>
        {freshEnrollmentNeedsPassword && <>
          <label htmlFor="mobile-enrollment-password">비밀번호 다시 확인</label>
          <input id="mobile-enrollment-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} disabled={busy} />
        </>}
        {error && <p role="alert" className="mobile-session-lock__error">{error}</p>}
        <Button type="button" disabled={busy || (freshEnrollmentNeedsPassword && !password)} onClick={() => void enrollPasskey(freshEnrollmentNeedsPassword)}>등록하기</Button>
        <Button type="button" variant="quiet" disabled={busy} onClick={() => {
          localStorage.setItem(`kop-passkey-enrollment-declined:${session.identity.id}`, '1');
          setEnrollmentOffer(false); setError(''); setPassword('');
        }}>나중에</Button>
      </section>
    </dialog>
  </>;
}
